import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Spinner, Sparkle, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
  asset_type: "vehicle", asset_id: "", name: "", details: {}, stock_number: "", listing_url: "",
  year: "", make: "", model: "", trim: "", vin: "", mileage: "", color: "", vehicle_type: "",
  recon_status: "", recon_date: "", recon_notes: "",
  location_id: "", status: "Available", assigned_employee_id: "", notes: "", description: "",
};

export default function AssetForm({ open, onOpenChange, asset, locations, employees, statuses, onSaved }) {
  const [types, setTypes] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [vinLoading, setVinLoading] = useState(false);

  const decodeVin = async () => {
    if (!form.vin) { toast.error("Enter a VIN first"); return; }
    setVinLoading(true);
    try {
      const { data } = await api.post("/assets/decode-vin", { vin: form.vin });
      setForm((prev) => ({ ...prev, ...data }));
      toast.success("Filled details from VIN");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setVinLoading(false);
    }
  };

  const generateDescription = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/assets/generate-description", {
        asset_type: form.asset_type, name: form.name, year: form.year, make: form.make,
        model: form.model, trim: form.trim, color: form.color, mileage: form.mileage,
        vin: form.vin, vehicle_type: form.vehicle_type, stock_number: form.stock_number,
      });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("Description generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenLoading(false);
    }
  };

  useEffect(() => { api.get("/asset-types").then(({ data }) => setTypes(data)).catch(() => {}); }, []);

  useEffect(() => {
    if (asset) setForm({ ...EMPTY, ...asset, details: asset.details || {}, location_id: asset.location_id || "", assigned_employee_id: asset.assigned_employee_id || "" });
    else setForm(EMPTY);
    // eslint-disable-next-line
  }, [asset, open]);

  const isVehicle = form.asset_type === "vehicle";
  const cfg = types[form.asset_type] || { fields: [] };
  const typeKeys = Object.keys(types);

  const getVal = (k) => (isVehicle ? (form[k] || "") : ((form.details || {})[k] || ""));
  const setVal = (k) => (e) => {
    if (isVehicle) setForm({ ...form, [k]: e.target.value });
    else setForm({ ...form, details: { ...(form.details || {}), [k]: e.target.value } });
  };
  const setValDirect = (k, val) => {
    if (isVehicle) setForm({ ...form, [k]: val });
    else setForm({ ...form, details: { ...(form.details || {}), [k]: val } });
  };
  const setTop = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.location_id) payload.location_id = null;
      if (!payload.assigned_employee_id) payload.assigned_employee_id = null;
      if (asset) await api.put(`/assets/${asset.id}`, payload);
      else await api.post("/assets", payload);
      toast.success(asset ? "Asset updated" : "Asset created");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-xl">{asset ? "Edit Asset" : "Add Asset"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-2">
          {asset && (
            <div className="sm:col-span-2">
              <Label className="text-sm font-semibold">Asset ID</Label>
              <Input value={form.asset_id || ""} onChange={setTop("asset_id")} className="h-11 mt-1.5 font-mono focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-asset_id" />
              <p className="text-xs text-zinc-400 mt-1.5">Unique ID for this asset (e.g. AST-00001). Change it to renumber and avoid duplicates.</p>
            </div>
          )}
          {/* Asset type */}
          <div>
            <Label className="text-sm font-semibold">Asset Type</Label>
            <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v, details: {} })} disabled={!!asset}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="asset-field-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {typeKeys.map((t) => <SelectItem key={t} value={t}>{types[t].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-semibold">Stock #</Label>
            <Input value={form.stock_number || ""} onChange={setTop("stock_number")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-stock_number" />
          </div>

          {/* Name for non-vehicle types */}
          {!isVehicle && (
            <div className="sm:col-span-2">
              <Label className="text-sm font-semibold">Name / Title</Label>
              <Input value={form.name || ""} onChange={setTop("name")} placeholder="e.g. CAT 320 Excavator" className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-name" />
            </div>
          )}

          {/* Type-specific fields */}
          {cfg.fields.map((f) => (
            <div key={f.key}>
              <div className="flex items-center justify-between min-h-[22px]">
                <Label className="text-sm font-semibold">{f.label}</Label>
                {f.key === "vin" && (
                  <Button type="button" variant="outline" size="sm" onClick={decodeVin} disabled={vinLoading} className="h-7 gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="asset-decode-vin">
                    {vinLoading ? <Spinner size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />} Auto-fill from VIN
                  </Button>
                )}
              </div>
              {f.type === "select" ? (
                <Select value={getVal(f.key) || "__none__"} onValueChange={(v) => setValDirect(f.key, v === "__none__" ? "" : v)}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid={`asset-field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={getVal(f.key)} onChange={setVal(f.key)} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid={`asset-field-${f.key}`} />
              )}
            </div>
          ))}

          {/* Job Site assignment — Tool, Equipment & Trailer only */}
          {["tool", "equipment", "trailer"].includes(form.asset_type) && (
            <div className="sm:col-span-2 rounded-lg border border-zinc-200 p-4 space-y-3" data-testid="asset-jobsite-section">
              <Label className="text-sm font-semibold flex items-center gap-2"><MapPin size={16} weight="fill" className="text-emerald-600" /> Job Site Assignment</Label>
              <div>
                <Label className="text-sm font-semibold">Job Site</Label>
                <Input value={getVal("job_site")} onChange={setVal("job_site")} placeholder="e.g. Riverside Bridge Project" className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-job_site" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Date on Site</Label>
                  <Input type="date" value={getVal("job_site_date")} onChange={setVal("job_site_date")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-job_site_date" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Expected Return Date</Label>
                  <Input type="date" value={getVal("job_site_return")} onChange={setVal("job_site_return")} className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-job_site_return" />
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-semibold">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="asset-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Location</Label>
            <Select value={form.location_id || "none"} onValueChange={(v) => setForm({ ...form, location_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="asset-field-location"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-semibold">Assigned Employee</Label>
            <Select value={form.assigned_employee_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_employee_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="asset-field-employee"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm font-semibold">Website Listing URL <span className="font-normal text-zinc-400">(optional)</span></Label>
            <Input value={form.listing_url || ""} onChange={setTop("listing_url")} placeholder="https://www.saveatsterling.com/viewdetails/..." className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="asset-field-listing_url" />
            <p className="text-xs text-zinc-400 mt-1.5">If set, scanning this asset's QR code opens this web page directly instead of the internal profile.</p>
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Description <span className="font-normal text-zinc-400">(AI listing copy)</span></Label>
              <Button type="button" variant="outline" size="sm" onClick={generateDescription} disabled={genLoading} className="h-8 gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="asset-generate-description">
                {genLoading ? <Spinner size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />} Generate with AI
              </Button>
            </div>
            <Textarea value={form.description || ""} onChange={setTop("description")} className="mt-1.5 focus:ring-2 focus:ring-emerald-500" rows={3} placeholder="Click 'Generate with AI' to create listing copy from the details above." data-testid="asset-field-description" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-sm font-semibold">Notes</Label>
            <Textarea value={form.notes || ""} onChange={setTop("notes")} className="mt-1.5 focus:ring-2 focus:ring-emerald-500" rows={3} data-testid="asset-field-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 border-zinc-300" data-testid="asset-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={loading} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="asset-form-save">
            {loading ? <Spinner size={18} className="animate-spin" /> : asset ? "Save Changes" : "Create Asset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
